import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n';
import type { WebSocketAdapter } from '../../../utils/WebSocketAdapter';
import { AppIcon } from '../icons';

const BAR_HEIGHT = 48;
const BAR_HEIGHT_COMPACT = 36;
const BAR_WIDTH_OPEN = 380;
/** Keep clear of SoundDrawer (same bottom-left stack): bar height + gap. */
const SOUND_DRAWER_GAP = 12;
const SOUND_DRAWER_OFFSET = BAR_HEIGHT + SOUND_DRAWER_GAP;
const MOBILE_COMPACT_MQ = '(max-width: 640px)';

interface HostControlDrawerProps {
  socket: WebSocketAdapter;
  roomName: string;
  isPaused?: boolean;
  onLeaveRoom: () => void;
  /** When true (default), sit above SoundDrawer. Host-as-player has no SoundDrawer. */
  clearOfSoundDrawer?: boolean;
}

const DrawerButton: React.FC<{
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}> = ({ label, onClick, variant = 'default' }) => (
  <button
    type="button"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }}
    style={{
      padding: '0.3rem 0.65rem',
      borderRadius: '6px',
      border: `1px solid ${variant === 'danger' ? 'rgba(248, 113, 113, 0.5)' : 'var(--border-medium)'}`,
      background: variant === 'danger' ? 'rgba(248, 113, 113, 0.15)' : 'rgba(var(--color-primary-rgb), 0.12)',
      color: '#F8FAFC',
      fontSize: '0.72rem',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      transition: 'background 0.2s, border-color 0.2s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background =
        variant === 'danger' ? 'rgba(248, 113, 113, 0.28)' : 'rgba(var(--color-primary-rgb), 0.22)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background =
        variant === 'danger' ? 'rgba(248, 113, 113, 0.15)' : 'rgba(var(--color-primary-rgb), 0.12)';
    }}
  >
    {label}
  </button>
);

const HostControlDrawer: React.FC<HostControlDrawerProps> = ({
  socket,
  roomName,
  isPaused = false,
  onLeaveRoom,
  clearOfSoundDrawer = true,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Host-as-player on mobile: smaller FAB tucked into the corner so it overlaps less.
  const [isCompactMobile, setIsCompactMobile] = useState(() =>
    typeof window !== 'undefined' && !clearOfSoundDrawer
      ? window.matchMedia(MOBILE_COMPACT_MQ).matches
      : false
  );

  useEffect(() => {
    if (clearOfSoundDrawer || typeof window === 'undefined') {
      setIsCompactMobile(false);
      return;
    }
    const mq = window.matchMedia(MOBILE_COMPACT_MQ);
    const update = () => setIsCompactMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [clearOfSoundDrawer]);

  const barHeight = isCompactMobile ? BAR_HEIGHT_COMPACT : BAR_HEIGHT;
  const edgeOffset = isCompactMobile ? '0.5rem' : '1.25rem';
  const maxWidthInset = isCompactMobile ? '1rem' : '2.5rem';

  const handleSkip = () => {
    socket.emit('host-skip-question', { roomName });
  };

  const handlePauseToggle = () => {
    socket.emit('pause-game', { roomName });
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: clearOfSoundDrawer
          ? `calc(1.25rem + ${SOUND_DRAWER_OFFSET}px)`
          : edgeOffset,
        left: edgeOffset,
        zIndex: 10002,
        height: `${barHeight}px`,
        width: open ? `${BAR_WIDTH_OPEN}px` : `${barHeight}px`,
        maxWidth: `calc(100vw - ${maxWidthInset})`,
        borderRadius: isCompactMobile ? '6px' : '8px',
        background: 'rgba(15, 23, 42, 0.88)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(var(--color-primary-rgb), 0.42)',
        boxShadow: '0 0 0 1px rgba(var(--color-primary-rgb), 0.18), 0 0 18px rgba(var(--color-primary-rgb), 0.28), var(--shadow-md)',
        transition: 'width 0.3s ease-out',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: 0,
        gap: 0,
        boxSizing: 'border-box',
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label={open ? t('common.tooltips.closeGameControls') : t('common.tooltips.openGameControls')}
        style={{
          width: `${barHeight}px`,
          minWidth: `${barHeight}px`,
          height: '100%',
          border: 'none',
          borderRight: open ? '1px solid var(--border-medium)' : 'none',
          borderRadius: open
            ? `${isCompactMobile ? 6 : 8}px 0 0 ${isCompactMobile ? 6 : 8}px`
            : `${isCompactMobile ? 6 : 8}px`,
          background: 'rgba(var(--color-primary-rgb), 0.08)',
          boxSizing: 'border-box',
          borderLeft: open ? '2px solid var(--color-primary)' : '1px solid rgba(var(--color-primary-rgb), 0.25)',
          color: '#F8FAFC',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          boxShadow: 'inset 0 0 12px rgba(var(--color-primary-rgb), 0.18)',
          transition: 'border 0.2s, background 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(var(--color-primary-rgb), 0.14)';
          e.currentTarget.style.boxShadow = 'inset 0 0 14px rgba(var(--color-primary-rgb), 0.24), 0 0 12px rgba(var(--color-primary-rgb), 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(var(--color-primary-rgb), 0.08)';
          e.currentTarget.style.boxShadow = 'inset 0 0 12px rgba(var(--color-primary-rgb), 0.18)';
        }}
      >
        <AppIcon name="settings" fontSize={isCompactMobile ? 'small' : 'medium'} />
      </button>

      {open && (
        <div
          role="group"
          aria-label={t('gameplay.hostControls.drawerAria')}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0 0.75rem',
            minWidth: 0,
          }}
        >
          <DrawerButton label={t('gameplay.hostControls.leaveRoom')} onClick={onLeaveRoom} variant="danger" />
          <DrawerButton label={t('gameplay.hostControls.skipQuestion')} onClick={handleSkip} />
          <DrawerButton label={isPaused ? t('gameplay.hostControls.resumeGame') : t('gameplay.hostControls.pauseGame')} onClick={handlePauseToggle} />
        </div>
      )}
    </div>
  );
};

export default HostControlDrawer;
